import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import PageTitle from '../headers/PageTitle';
import ActionRollupSummary from '../IAP/ActionRollupSummary';
import { MyTasksTable } from './MyTasksTable';
import {
  filterMyTasks,
  groupMyTasks,
  taskHref,
  type MyTaskGroup,
} from './myTasksTableModel';
import { useMyTasks, type MyTask } from './useMyTasks';

/**
 * Every task assigned to the signed-in user, across their organization and
 * every incident they are on.
 *
 * Deliberately separate from `/task_management`, which is the unrelated
 * Critical Tasks feature. Nothing here is editable in place: rows deep-link to
 * the scope that owns the task, so there is one place a task is edited rather
 * than two that can disagree.
 */
const MyTasksPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useMyTasks();
  const [search, setSearch] = useState('');

  const tasks = data?.tasks ?? [];
  const groups = useMemo(
    () => groupMyTasks(filterMyTasks(tasks, search)),
    [tasks, search]
  );

  const emptyMessage = search.trim()
    ? `No tasks match “${search}”.`
    : 'Nothing is assigned to you right now.';

  return (
    <div className="p-4 pt-0">
      <PageTitle title="My Tasks" />
      <p className="text-muted mb-4">
        Everything assigned to you, your ICS position or your strike team,
        across this organization and every incident you are on.
      </p>

      {isError ? (
        <p className="text-danger">
          Your tasks could not be loaded. Refresh to try again.
        </p>
      ) : (
        <>
          <ActionRollupSummary
            rollup={data?.rollup}
            isLoading={isLoading}
            groupingKey="scope"
            className="pb-3"
          />
          <MyTasksTable
            groups={groups}
            search={search}
            onSearchChange={setSearch}
            onOpenTask={(task: MyTask) => navigate(taskHref(task))}
            onOpenGroup={(group: MyTaskGroup) => navigate(group.href)}
            emptyMessage={emptyMessage}
          />
        </>
      )}
    </div>
  );
};

export default MyTasksPage;
